import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../api/api-base.service';
import { AuthService } from '../../auth/services/auth.service';
import { NativePushService } from '../mobile/native-push.service';
import { MobilePlatformService } from '../mobile/mobile-platform.service';

/**
 * INFRASEG - Push Notification Service
 * Suporta FCM (Firebase) e, como fallback, Web Push (VAPID).
 */

// Interfaces
interface SubscriptionStatus {
    isSubscribed: boolean;
    isHealthy: boolean;
    lastCheck: Date | null;
    endpoint: string | null;
    error: string | null;
}

interface ServiceWorkerStatus {
    isRegistered: boolean;
    isActive: boolean;
    scope: string | null;
    updateFound: boolean;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService implements OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly apiService = inject(ApiService);
    private readonly authService = inject(AuthService);
    private readonly nativePush = inject(NativePushService);
    private readonly mobilePlatform = inject(MobilePlatformService);
    private readonly vapidPublicKey = environment.vapidPublicKey;

    /** Usar FCM quando firebase está configurado no environment */
    private get useFcm(): boolean {
        const fb = (environment as { firebase?: unknown }).firebase;
        return !!fb && typeof fb === 'object' && 'apiKey' in fb && !!((fb as { apiKey?: string }).apiKey);
    }

    private swRegistration: ServiceWorkerRegistration | null = null;
    private currentSubscription: PushSubscription | null = null;
    /** Token FCM atual (para unregister) */
    private currentFcmToken: string | null = null;

    // Reactive state com signals
    private readonly subscriptionStatus = signal<SubscriptionStatus>({
        isSubscribed: false,
        isHealthy: false,
        lastCheck: null,
        endpoint: null,
        error: null,
    });

    private readonly swStatus = signal<ServiceWorkerStatus>({
        isRegistered: false,
        isActive: false,
        scope: null,
        updateFound: false,
    });

    // Computed signals públicos
    readonly isSubscribed = computed(() => this.subscriptionStatus().isSubscribed);
    readonly isHealthy = computed(() => this.subscriptionStatus().isHealthy);
    readonly getSubscriptionStatus = computed(() => this.subscriptionStatus());
    readonly getServiceWorkerStatus = computed(() => this.swStatus());

    // Health check
    private readonly HEALTH_CHECK_INTERVAL = 60000; // 1 minuto
    private readonly RECOVERY_RETRY_INTERVAL = 30000; // 30 segundos
    private healthCheckInterval: any = null;
    private recoveryAttempts = 0;
    private readonly MAX_RECOVERY_ATTEMPTS = 3;

    // Cleanup
    private readonly destroy$ = new Subject<void>();

    // Detecção de mobile
    private readonly isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    private readonly isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    constructor() {
        if (this.mobilePlatform.isNative()) {
            return;
        }
        if (!this.useFcm) {
            this.registerServiceWorker().then(() => {}).catch((err) => {
                console.warn('[PushNotification] ⚠️ Service Worker não registrado:', err?.message ?? err);
            });
        }
    }

    private isNativeApp(): boolean {
        return this.mobilePlatform.isNative();
    }

    /**
     * Verifica se push notifications são suportadas
     */
    isSupported(): boolean {
        if (this.isNativeApp()) {
            return this.nativePush.isAvailable();
        }

        const hasNotification = 'Notification' in window;
        const hasServiceWorker = 'serviceWorker' in navigator;
        const hasPushManager = 'PushManager' in window;

        const isSupported = hasNotification && hasServiceWorker && hasPushManager;

        if (!isSupported) {
            console.warn('[PushNotification] ⚠️ Push Notifications NÃO suportadas:', {
                hasNotification,
                hasServiceWorker,
                hasPushManager,
            });
        }

        return isSupported;
    }

    /**
     * Verifica se já tem permissão
     */
    async hasPermission(): Promise<boolean> {
        if (this.isNativeApp()) {
            return this.nativePush.checkPermission();
        }
        if (!this.isSupported()) return false;
        return Notification.permission === 'granted';
    }

    /**
     * Solicita permissão e cria subscription
     */
    async solicitarPermissao(): Promise<NotificationPermission> {
        if (!this.isSupported()) {
            throw new Error('Push notifications não são suportadas neste navegador');
        }

        if (this.isNativeApp()) {
            const granted = await this.nativePush.requestPermission();
            return granted ? 'granted' : 'denied';
        }

        // iOS tem comportamento especial
        if (this.isiOS) {
            // Verificar se iOS suporta (16.4+)
            const userAgent = navigator.userAgent;
            const iosVersion = userAgent.match(/OS (\d+)_(\d+)/);

            if (iosVersion) {
                const major = parseInt(iosVersion[1]);
                const minor = parseInt(iosVersion[2]);

                if (major < 16 || (major === 16 && minor < 4)) {
                    throw new Error(`iOS ${major}.${minor} não suporta Push Notifications (requer 16.4+)`);
                }
            }
        }

        const permission = await Notification.requestPermission();
        return permission;
    }

    /**
     * Inicializa push notifications (FCM ou Web Push) e envia token/subscription ao backend.
     * Requer usuário autenticado.
     */
    async inicializar(): Promise<boolean> {
        try {
            const isAuthenticated = this.authService.authStateSignal().isAuthenticated;
            if (!isAuthenticated) {
                throw new Error('Usuário não autenticado. Faça login para ativar push notifications.');
            }
            if (!this.isSupported()) {
                throw new Error('Push notifications não são suportadas');
            }

            if (this.isNativeApp()) {
                const ok = await this.nativePush.register();
                if (ok) {
                    this.updateSubscriptionStatus({
                        isSubscribed: true,
                        isHealthy: true,
                        lastCheck: new Date(),
                        endpoint: 'native',
                        error: null,
                    });
                }
                return ok;
            }

            const hasPermission = await this.hasPermission();
            if (!hasPermission) {
                throw new Error('Permissão de notificações não concedida');
            }

            if (this.useFcm) {
                return await this.inicializarFcm();
            }
            return await this.inicializarWebPush();
        } catch (error: any) {
            console.error('[PushNotification] ❌ ERRO ao inicializar:', error);
            this.updateSubscriptionStatus({
                isSubscribed: false,
                isHealthy: false,
                lastCheck: new Date(),
                endpoint: null,
                error: error?.message || 'Erro desconhecido',
            });
            return false;
        }
    }

    /** Fluxo FCM: obtém token via loader e envia ao backend */
    private async inicializarFcm(): Promise<boolean> {
        const fb = (environment as { firebase?: Record<string, string> }).firebase;
        if (!fb) throw new Error('Firebase não configurado');

        const { getFcmToken } = await import('./fcm-token.loader');
        const token = await getFcmToken(fb);
        if (!token) {
            console.warn('[PushNotification] FCM não disponível. Use Web Push ou ative FCM (ver fcm-token.loader.ts).');
            return this.inicializarWebPush();
        }
        await firstValueFrom(
            this.apiService.post('notifications/push/register', { token }, { showLoader: false })
        );
        this.currentFcmToken = token;
        this.updateSubscriptionStatus({
            isSubscribed: true,
            isHealthy: true,
            lastCheck: new Date(),
            endpoint: token.substring(0, 50) + '...',
            error: null,
        });
        return true;
    }

    /** Fluxo Web Push (VAPID): subscription e envio ao backend */
    private async inicializarWebPush(): Promise<boolean> {
        if (!this.vapidPublicKey || this.vapidPublicKey.length < 80) {
            throw new Error('VAPID Public Key inválida ou não configurada');
        }
        if (!this.swRegistration) {
            await this.registerServiceWorker();
        }
        await this.waitForServiceWorkerActive();
        await new Promise((r) => setTimeout(r, 2000));
        const subscription = await this.getOrCreateSubscription();
        if (!subscription) throw new Error('Falha ao criar subscription');
        await this.enviarSubscriptionParaBackend(subscription);
        this.currentSubscription = subscription;
        this.updateSubscriptionStatus({
            isSubscribed: true,
            isHealthy: true,
            lastCheck: new Date(),
            endpoint: subscription.endpoint,
            error: null,
        });
        this.startHealthChecks();
        this.setupMessageListeners();
        return true;
    }

    /**
     * Remove subscription (FCM token ou Web Push)
     */
    async removerSubscription(): Promise<void> {
        try {
            if (this.isNativeApp()) {
                await this.nativePush.unregister();
                this.updateSubscriptionStatus({
                    isSubscribed: false,
                    isHealthy: false,
                    lastCheck: new Date(),
                    endpoint: null,
                    error: null,
                });
                return;
            }
            if (this.useFcm && this.currentFcmToken) {
                await firstValueFrom(
                    this.apiService.post('notifications/push/unregister', { token: this.currentFcmToken }, { showLoader: false })
                );
                this.currentFcmToken = null;
            } else if (this.swRegistration) {
                const subscription = await this.swRegistration.pushManager.getSubscription();
                if (subscription) {
                    await firstValueFrom(
                        this.apiService.post('notifications/push/unsubscribe', { endpoint: subscription.endpoint }, { showLoader: false })
                    );
                    await subscription.unsubscribe();
                }
                this.currentSubscription = null;
                this.stopHealthChecks();
            }
            this.updateSubscriptionStatus({
                isSubscribed: false,
                isHealthy: false,
                lastCheck: new Date(),
                endpoint: null,
                error: null,
            });
        } catch (error) {
            console.error('[PushNotification] ❌ Erro ao remover subscription:', error);
            throw error;
        }
    }

    // ========================================
    // MÉTODOS PRIVADOS - SERVICE WORKER
    // ========================================

    /**
     * Registra o Service Worker
     */
    private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
        try {
            // Usar o novo Service Worker otimizado
            const registration = await navigator.serviceWorker.register('/push-sw.js', {
                scope: '/',
            });

            this.swRegistration = registration;

            // Atualizar estado
            this.swStatus.set({
                isRegistered: true,
                isActive: !!registration.active,
                scope: registration.scope,
                updateFound: !!registration.waiting,
            });

            // Listener para atualizações
            registration.addEventListener('updatefound', () => {
                this.swStatus.update(status => ({
                    ...status,
                    updateFound: true,
                }));
            });

            return registration;
        } catch (error) {
            console.error('[PushNotification] ❌ Erro ao registrar Service Worker:', error);
            throw error;
        }
    }

    /**
     * Aguarda o Service Worker estar ativo
     */
    private async waitForServiceWorkerActive(): Promise<void> {
        if (!this.swRegistration) {
            throw new Error('Service Worker não registrado');
        }

        // Se já estiver ativo, retornar imediatamente
        if (this.swRegistration.active) {
            return;
        }

        // Aguardar instalação
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Timeout aguardando Service Worker ativar'));
            }, 30000); // 30 segundos

            const checkActive = () => {
                if (this.swRegistration!.active) {
                    clearTimeout(timeout);
                    resolve();
                } else {
                    setTimeout(checkActive, 100);
                }
            };

            checkActive();
        });
    }

    /**
     * Obtém ou cria uma nova subscription
     */
    private async getOrCreateSubscription(): Promise<PushSubscription> {
        if (!this.swRegistration) {
            throw new Error('Service Worker não registrado');
        }

        try {
            // Verificar se já existe subscription
            let subscription = await this.swRegistration.pushManager.getSubscription();

            if (subscription) {
                return subscription;
            }

            // Criar nova subscription
            const applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

            subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey,
            });

            return subscription;
        } catch (error: any) {
            console.error('[PushNotification] ❌ Erro ao obter/criar subscription:', error);
            throw error;
        }
    }

    /**
     * Envia subscription para o backend
     */
    private async enviarSubscriptionParaBackend(subscription: PushSubscription): Promise<void> {
        try {
            const p256dhKey = subscription.getKey('p256dh');
            const authKey = subscription.getKey('auth');

            if (!p256dhKey || !authKey) {
                throw new Error('Keys da subscription não encontradas');
            }

            const subscriptionData = {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: this.arrayBufferToBase64(p256dhKey),
                    auth: this.arrayBufferToBase64(authKey),
                },
            };

            await firstValueFrom(
                this.apiService.post('notifications/push/subscribe', subscriptionData, {
                    showLoader: false
                })
            );
        } catch (error: any) {
            console.error('[PushNotification] ❌ Erro ao enviar subscription:', error);
            throw error;
        }
    }

    // ========================================
    // HEALTH CHECKS E AUTO-RECOVERY
    // ========================================

    /**
     * Inicia health checks periódicos
     */
    private startHealthChecks(): void {
        // Limpar interval anterior se existir
        this.stopHealthChecks();

        // Health check a cada minuto
        this.healthCheckInterval = setInterval(() => {
            this.performHealthCheck();
        }, this.HEALTH_CHECK_INTERVAL);

        // Primeiro check imediato
        setTimeout(() => this.performHealthCheck(), 5000);
    }

    /**
     * Para health checks
     */
    private stopHealthChecks(): void {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
    }

    /**
     * Executa health check da subscription
     */
    private async performHealthCheck(): Promise<void> {
        try {
            if (!this.swRegistration) {
                throw new Error('Service Worker não registrado');
            }

            // Verificar se subscription ainda existe
            const subscription = await this.swRegistration.pushManager.getSubscription();

            if (!subscription) {
                console.warn('[PushNotification] ⚠️ Subscription perdida! Tentando recuperar...');
                await this.attemptRecovery();
                return;
            }

            // Verificar se endpoint é o mesmo
            if (this.currentSubscription && subscription.endpoint !== this.currentSubscription.endpoint) {
                console.warn('[PushNotification] ⚠️ Endpoint mudou! Atualizando backend...');
                await this.enviarSubscriptionParaBackend(subscription);
                this.currentSubscription = subscription;
            }

            // Ping no Service Worker
            const isAlive = await this.pingServiceWorker();

            if (!isAlive) {
                console.warn('[PushNotification] ⚠️ Service Worker não respondeu ao ping!');
            }

            // Atualizar estado
            this.updateSubscriptionStatus({
                isSubscribed: true,
                isHealthy: isAlive,
                lastCheck: new Date(),
                endpoint: subscription.endpoint,
                error: null,
            });

            // Resetar tentativas de recovery
            this.recoveryAttempts = 0;
        } catch (error: any) {
            console.error('[PushNotification] ❌ Health check falhou:', error);

            this.updateSubscriptionStatus({
                isSubscribed: false,
                isHealthy: false,
                lastCheck: new Date(),
                endpoint: this.currentSubscription?.endpoint || null,
                error: error.message,
            });

            // Tentar recovery
            await this.attemptRecovery();
        }
    }

    /**
     * Tenta recuperar subscription perdida
     */
    private async attemptRecovery(): Promise<void> {
        if (this.recoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
            console.error('[PushNotification] ❌ Máximo de tentativas de recovery atingido');
            return;
        }

        this.recoveryAttempts++;

        try {
            // Aguardar um pouco antes de tentar
            await new Promise(resolve => setTimeout(resolve, this.RECOVERY_RETRY_INTERVAL));

            // Tentar reinicializar
            const success = await this.inicializar();

            if (success) {
                this.recoveryAttempts = 0;
            } else {
                console.error('[PushNotification] ❌ Recovery falhou');
            }
        } catch (error) {
            console.error('[PushNotification] ❌ Erro durante recovery:', error);
        }
    }

    /**
     * Envia ping para o Service Worker
     */
    private async pingServiceWorker(): Promise<boolean> {
        try {
            if (!this.swRegistration || !this.swRegistration.active) {
                return false;
            }

            return new Promise<boolean>((resolve) => {
                const messageChannel = new MessageChannel();

                messageChannel.port1.onmessage = (event) => {
                    if (event.data && event.data.type === 'PONG') {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                };

                this.swRegistration!.active!.postMessage(
                    { type: 'PING' },
                    [messageChannel.port2]
                );

                // Timeout de 5 segundos
                setTimeout(() => resolve(false), 5000);
            });
        } catch (error) {
            console.error('[PushNotification] ❌ Erro ao pingar Service Worker:', error);
            return false;
        }
    }

    // ========================================
    // MESSAGE LISTENERS
    // ========================================

    /**
     * Configura listeners de mensagens do Service Worker
     */
    private setupMessageListeners(): void {
        if (!navigator.serviceWorker) {
            return;
        }

        navigator.serviceWorker.addEventListener('message', (event) => {
            const message = event.data;

            if (!message || !message.type) {
                return;
            }

            switch (message.type) {
                case 'SW_HEARTBEAT':
                    break;

                case 'SUBSCRIPTION_LOST':
                    // Subscription foi perdida
                    console.warn('[PushNotification] ⚠️ SW reportou perda de subscription');
                    this.attemptRecovery();
                    break;

                case 'NOTIFICATION_CLICK':
                    // Aqui você pode navegar para a rota específica
                    // this.router.navigate([message.url]);
                    break;

                case 'GET_AUTH_TOKEN':
                    // Enviar token via postMessage
                    // this.sendAuthTokenToSW();
                    break;
            }
        });
    }

    // ========================================
    // UTILITÁRIOS
    // ========================================

    /**
     * Atualiza status da subscription
     */
    private updateSubscriptionStatus(updates: Partial<SubscriptionStatus>): void {
        this.subscriptionStatus.update(current => ({
            ...current,
            ...updates,
        }));
    }

    /**
     * Converte URL-safe Base64 para Uint8Array
     */
    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Converte ArrayBuffer para base64
     */
    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    // ========================================
    // CLEANUP
    // ========================================

    ngOnDestroy(): void {
        this.stopHealthChecks();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
